// texe-web/src/components/TranslateButton.tsx
import { useState, useMemo } from "react";
import type { CSSProperties } from "react";
import i18n from "../i18n";
import { apiTranslate } from "../lib/api";

type TranslateButtonProps = {
  /** Çevrilecek metin */
  text: string;
  /** Kaynak dil (yoksa heuristic/auto) */
  sourceLang?: string;
  /** Hedef dil; boşsa i18n.language kullanılır */
  targetLang?: string;
  /** Buton ve kutu boyutu */
  size?: "sm" | "md";
  /** Ek sınıf adı (opsiyonel) */
  className?: string;
};

export default function TranslateButton({
  text,
  sourceLang,
  targetLang,
  size = "md",
  className,
}: TranslateButtonProps) {
  const [translated, setTranslated] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hedef dili belirle (prop > i18n.language > "tr")
  const target = useMemo(
    () => (targetLang || i18n.language || "tr").slice(0, 2).toLowerCase(),
    [targetLang]
  );

  // kaba İngilizce tespiti (ASCII çoğunluk)
  const looksEnglish = (s: string) => !/[^\x00-\x7F]/.test(s);

  async function handleTranslate() {
    const t = text?.trim();
    if (!t || loading) return;

    setLoading(true);
    setError(null);
    setNote(null);
    setTranslated(null);

    try {
      // 1) ilk deneme: verilen source yoksa heuristic
      const firstSource = sourceLang ?? (looksEnglish(t) ? "en" : "auto");
      let out = await apiTranslate(t, target, firstSource);

      // 2) sonuç değişmediyse, tek fallback denemesi yap
      if (!out || out.trim() === t) {
        const fallbackSource = firstSource === "en" ? "auto" : "en";
        try {
          out = await apiTranslate(t, target, fallbackSource);
          if (!out || out.trim() === t) {
            setNote(
              "Çeviri metni değişmedi (servis çevirememiş olabilir ya da metin zaten bu dilde)."
            );
          } else {
            setNote(`(Kaynak dili '${fallbackSource}' olarak deneyerek çevrildi)`);
          }
        } catch {
          setNote("İkinci deneme de sonuç vermedi.");
        }
      }

      setTranslated(out || t);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Çeviri başarısız oldu");
    } finally {
      setLoading(false);
    }
  }

  const btnStyle: CSSProperties = {
    borderRadius: 8,
    border: "1px solid var(--muted)",
    background: "var(--btn-bg, #0f0f0f)",
    color: "var(--text, #eee)",
    padding: size === "sm" ? "4px 8px" : "6px 10px",
    fontSize: size === "sm" ? 12 : 14,
    cursor: loading ? "not-allowed" : "pointer",
  };

  const boxStyle: CSSProperties = {
    marginTop: 6,
    padding: size === "sm" ? "6px 8px" : "8px 10px",
    border: "1px solid #2c2c2c",
    borderRadius: 6,
    background: "#111",
    color: "#ddd",
    whiteSpace: "pre-wrap",
    fontSize: size === "sm" ? 13 : 14,
    lineHeight: 1.45,
  };

  const targetLabel = target === "tr" ? "Türkçe" : target.toUpperCase();
  return null; // 👈 geçici olarak gizlendi
  return (
    <div className={className} style={{ marginTop: 8 }}>
      {!translated ? (
        <button
          onClick={handleTranslate}
          disabled={loading || !text?.trim()}
          style={btnStyle}
          title={`Translate → ${targetLabel}`}
          aria-busy={loading}
        >
          {loading ? "Çevriliyor..." : `${targetLabel} diline çevir`}
        </button>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
            <button onClick={() => setTranslated(null)} style={btnStyle}>
              Gizle
            </button>
            <button onClick={handleTranslate} disabled={loading} style={btnStyle}>
              {loading ? "Yeniden..." : "Tekrar dene"}
            </button>
          </div>
          <div style={boxStyle}>
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
              Çeviri hedefi: <b>{targetLabel}</b>
              {note ? <span style={{ marginLeft: 6, color: "#aaa" }}>• {note}</span> : null}
            </div>
            {translated}
          </div>
        </>
      )}
      {error && <div style={{ color: "crimson", marginTop: 6 }}>{error}</div>}
    </div>
  );
}
